import {useEffect, useState} from 'react';
import {useParams, Link} from 'react-router-dom';
import {Button, Collapse, Spin, Typography, Card, Row, Col} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {PlacementTestsService} from './PlacementTestsService';
import {QuestionsService} from '../Questions/QuestionsService';

const placementTestsService = new PlacementTestsService();
const questionsService = new QuestionsService();

export const PlacementTestDetails = () => {
  const [placementTest, setPlacementTest] = useState<any>(undefined);
  const [detailedQuestions, setDetailedQuestions] = useState<any[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  let params: {testId: string} = useParams();

  const {Title, Text} = Typography;
  const {Panel} = Collapse;

  useEffect(() => {
    placementTestsService
      .getPlacementTestById(params.testId)
      .then(async resp => {
        console.log('📋 Placement Test response:', resp);
        console.log('📋 Questions from response:', resp?.questions);
        setPlacementTest(resp);

        // Check if questions exist and what structure they have
        if (resp?.questions && resp.questions.length > 0) {
          console.log('📋 Number of questions:', resp.questions.length);
          console.log('📋 First question structure:', resp.questions[0]);

          // Check if questions are already populated with data or just IDs
          const firstQuestion = resp.questions[0];
          const isPopulated =
            firstQuestion &&
            typeof firstQuestion === 'object' &&
            (firstQuestion.questionText ||
              firstQuestion.text ||
              firstQuestion.name);

          console.log('📋 Questions are populated:', isPopulated);

          if (isPopulated) {
            // Questions are already populated, use them directly
            console.log('✅ Using populated questions directly');
            setDetailedQuestions(resp.questions);
          } else {
            // Questions might be just IDs or references, fetch detailed data
            console.log('🔍 Fetching detailed question data...');
            const questionPromises = resp.questions.map((q: any) => {
              // Handle different possible structures
              const questionId = typeof q === 'string' ? q : q?._id || q?.id;
              console.log('🔍 Fetching question ID:', questionId);
              return questionId
                ? questionsService.getQuestionById(questionId)
                : Promise.resolve(null);
            });

            try {
              const detailedQuestionsData = await Promise.all(questionPromises);
              const validQuestions = detailedQuestionsData.filter(
                q => q !== null,
              );
              console.log(
                '✅ Fetched detailed questions:',
                validQuestions.length,
              );
              setDetailedQuestions(validQuestions);
            } catch (error) {
              console.error('❌ Error fetching detailed questions:', error);
              // Fallback: try to use whatever question data we have
              setDetailedQuestions(resp.questions);
            }
          }
        } else {
          console.log('📋 No questions in response');
          setDetailedQuestions([]);
        }

        setIsLoading(false);
      })
      .catch(error => {
        console.log('❌ Error fetching placement test:', error);
        setIsLoading(false);
        setIsError(true);
      });
  }, []);

  return (
    <div style={{padding: 20}}>
      <Button icon={<ArrowLeftOutlined />} style={{marginBottom: 20}}>
        <Link to="/placement-tests">Back to List</Link>
      </Button>

      <Title level={2} style={{textAlign: 'center'}}>
        Placement Test Details
      </Title>

      {!isError && !isLoading && placementTest && (
        <div>
          <Card title="Test Information" style={{marginBottom: 20}}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Name: </Text>
                  {placementTest?.name}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>ID: </Text>
                  {placementTest?.id}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Duration: </Text>
                  {placementTest?.duration || '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Created By: </Text>
                  {placementTest?.createdBy || '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Start Date: </Text>
                  {placementTest?.startDate
                    ? new Date(placementTest.startDate).toLocaleString()
                    : '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Expiration Date: </Text>
                  {placementTest?.expirationDate
                    ? new Date(placementTest.expirationDate).toLocaleString()
                    : '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Created At: </Text>
                  {placementTest?.createdAt
                    ? new Date(placementTest.createdAt).toLocaleString()
                    : '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{display: 'block'}}>
                  <Text strong>Updated At: </Text>
                  {placementTest?.updatedAt
                    ? new Date(placementTest.updatedAt).toLocaleString()
                    : '-'}
                </Text>
              </Col>
              <Col span={24}>
                <Text style={{display: 'block'}}>
                  <Text strong>Description: </Text>
                  {placementTest?.description || '-'}
                </Text>
              </Col>
              {placementTest?.thumbnail && (
                <Col span={24}>
                  <Text style={{display: 'block'}}>
                    <Text strong>Thumbnail: </Text>
                    <a
                      href={placementTest.thumbnail}
                      target="_blank"
                      rel="noopener noreferrer">
                      View Thumbnail
                    </a>
                  </Text>
                </Col>
              )}
            </Row>
          </Card>

          <Card title={`Questions (${detailedQuestions.length})`}>
            {detailedQuestions.length === 0 ? (
              <Text>No questions found for this placement test.</Text>
            ) : (
              <Collapse defaultActiveKey={['0']}>
                {detailedQuestions.map((question: any, index: number) => {
                  return (
                    <Panel
                      key={question?.id || question?._id || index}
                      header={
                        'Question ' +
                        (index + 1) +
                        ': ' +
                        (question?.name || 'Unnamed Question')
                      }>
                      {question?.name && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Name: </Text>
                          {question?.name}
                        </Text>
                      )}
                      {(question?.questionText || question?.text) && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Question Text: </Text>
                          {question?.questionText || question?.text}
                        </Text>
                      )}
                      {(question?.AiText || question?.textAI) && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>AI Text: </Text>
                          {question?.AiText || question?.textAI}
                        </Text>
                      )}
                      {question?.type && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Type: </Text>
                          {question?.type}
                        </Text>
                      )}
                      {question?.difficulty && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Difficulty: </Text>
                          {question?.difficulty}
                        </Text>
                      )}
                      {question?.summary && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Summary: </Text>
                          {question?.summary}
                        </Text>
                      )}
                      {question?.description && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Description: </Text>
                          {question?.description}
                        </Text>
                      )}
                      {question?.payload && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Payload: </Text>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              background: '#f5f5f5',
                              padding: 10,
                              borderRadius: 4,
                            }}>
                            {JSON.stringify(question?.payload, null, 2)}
                          </pre>
                        </Text>
                      )}
                      {question?.createdAt && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Created At: </Text>
                          {new Date(question?.createdAt).toLocaleString()}
                        </Text>
                      )}
                      {question?.updatedAt && (
                        <Text style={{display: 'block', marginBottom: 8}}>
                          <Text strong>Updated At: </Text>
                          {new Date(question?.updatedAt).toLocaleString()}
                        </Text>
                      )}
                    </Panel>
                  );
                })}
              </Collapse>
            )}
          </Card>
        </div>
      )}

      {isLoading && (
        <Spin
          size="large"
          style={{margin: 'auto', width: '100%', marginTop: 50}}
        />
      )}
      {isError && (
        <Title level={3} style={{textAlign: 'center', color: 'red'}}>
          Placement Test fetching error ⚠️
        </Title>
      )}
    </div>
  );
};
